import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchCapabilities } from "../hooks/CapabilityHooks";
import { useFetchUserGroups } from "../hooks/UserGroupHooks";
import { CapabilityDTO } from "../types/capabilityDTO";
import { StationDTO } from "../types/stationDTO";
import { useFetchStations } from "../hooks/StationHooks";
import { UserDTO } from "../types/userDTO";
import { useTranslation } from 'react-i18next';
import { fi } from 'date-fns/locale';
import { registerLocale } from "react-datepicker";
import { useResendWelcomeMessage } from "../hooks/UserHooks";
import { toast } from "react-toastify";
import { UserGroupDTO } from "../types/userGroupDTO";

registerLocale('fi', fi);

type UserFormArgs = {
  headerText: string;
  user: UserDTO;
  submitted: (user: UserDTO) => void;
  deleted?: (user: UserDTO) => void;
};

const UserForm = ({ headerText, user: initialUser, submitted, deleted }: UserFormArgs) => {
  const { t, i18n } = useTranslation();
  const { organisationId } = useParams<{ organisationId: string }>();
  if (!organisationId) throw new Error("Missing organisationId from params");
  const nav = useNavigate();
  const hardCodedRoles = ["OptiviAdmin", "OptiviCommandUser", "OptiviMobileUser", "OptiviStationUser", "StationAdmin", "DataExport"];

  const [capabilities, setCapabilities] = useState<CapabilityDTO[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroupDTO[]>([]);
  const [userState, setUserState] = useState(initialUser);
  const [stations, setStations] = useState<StationDTO[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const { data: capabilitiesData, isLoading: capabilitiesIsLoading, isError: capabilitiesIsError } = useFetchCapabilities(organisationId);
  const { data: userGroupsData, isLoading: userGroupsIsLoading, isError: userGroupsIsError } = useFetchUserGroups(organisationId);
  const { data: stationsData, isLoading: stationsIsLoading, isError: stationsIsError } = useFetchStations(organisationId);
  const resendWelcomeMessageMutation = useResendWelcomeMessage(organisationId, userState.id);


  useEffect(() => {
    if (capabilitiesData) {
      setCapabilities(capabilitiesData);
    }
  }, [capabilitiesData]);

    useEffect(() => {
        if (userGroupsData) {
            setUserGroups(userGroupsData);
        }
    }, [userGroupsData]);

  useEffect(() => {
    if (stationsData) {
      setStations(stationsData);
    }
  }, [stationsData]);

  if (capabilitiesIsLoading || stationsIsLoading || userGroupsIsLoading) return <div>Loading...</div>;
  if (capabilitiesIsError || stationsIsError || userGroupsIsError) return <div>Error</div>;


  const onSubmit: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    submitted(userState);
  };

  const onCancel: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    nav(-1);
  };

  const onDelete: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    deleted?.(userState);
  };

  const onResendWelcomeMessage: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();
    setIsButtonDisabled(true);

    resendWelcomeMessageMutation.mutate(userState, {
      onSuccess: () => {
        toast.success(t('user.welcomeMessageSent'));
      },
      onError: () => {
        toast.error(t('user.welcomeMessageError'));
      },
      onSettled: () => {
        setTimeout(() => {
          setIsButtonDisabled(false);
        }, 3000);
      }
    });
  };

  const handleCapabilityChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    const capability: CapabilityDTO = capabilities.find(c => c.id === value)!;
    if (checked) {
      setUserState(prevState => ({
        ...prevState,
        capabilities: [...prevState.capabilities, capability]
      }));
    } else {
      setUserState(prevState => ({
        ...prevState,
        capabilities: prevState.capabilities.filter(c => c.id !== value)
      }));
    }
    };

    const handleUserGroupChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        const userGroup: UserGroupDTO = userGroups.find(g => g.id === value)!;
        if (checked) {
            setUserState(prevState => ({
                ...prevState,
                userGroups: [...prevState.userGroups, userGroup]
            }));
        } else {
            setUserState(prevState => ({
                ...prevState,
                userGroups: prevState.userGroups.filter(c => c.id !== value)
            }));
        }
    };

  const handleRoleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    const role: string = hardCodedRoles.find(c => c === value)!;
    if (checked) {
      setUserState(prevState => ({
        ...prevState,
        roles: [...prevState.roles, role]
      }));
    } else {
      setUserState(prevState => ({
        ...prevState,
        roles: prevState.roles.filter(c => c !== value)
      }));
    }
  };

  const handleStationChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    const station: StationDTO = stations.find(c => c.id === value)!;
    if (checked) {
      setUserState(prevState => ({
        ...prevState,
        stations: [...prevState.stations, station]
      }));
    } else {
      setUserState(prevState => ({
        ...prevState,
        stations: prevState.stations.filter(c => c.id !== value)
      }));
    }
  };

  const CapabilityRow = ({ capability }: { capability: CapabilityDTO }) => (
    <tr key={capability.id}>
      <td>
        <input
          type="checkbox"
          className="form-check-input"
          id={capability.id}
          key={capability.id}
          value={capability.id}
          checked={userState.capabilities.some(c => c.id === capability.id)}
          onChange={handleCapabilityChange}
        />
      </td>
      <td>
        <label className="form-check-label" htmlFor={capability.id}>
          {capability.name}
        </label>
      </td>
    </tr>
    );

    const UserGroupRow = ({ userGroup }: { userGroup: UserGroupDTO }) => (
        <tr key={userGroup.id}>
            <td>
                <input
                    type="checkbox"
                    className="form-check-input"
                    id={userGroup.id}
                    key={userGroup.id}
                    value={userGroup.id}
                    checked={userState.userGroups.some(c => c.id === userGroup.id)}
                    onChange={handleUserGroupChange}
                />
            </td>
            <td>
                <label className="form-check-label" htmlFor={userGroup.id}>
                    {userGroup.name}
                </label>
            </td>
        </tr>
    );

    const StationRow = ({ station }: { station: StationDTO }) => (
        <tr key={station.id}>
            <td>
                <input
                    type="checkbox"
                    className="form-check-input"
                    id={station.id}
                    key={station.id}
                    value={station.id}
                    checked={userState.stations.some(c => c.id === station.id)}
                    onChange={handleStationChange}
                />
            </td>
            <td>
                <label className="form-check-label" htmlFor={station.id}>
                    {station.name}
                </label>
            </td>
        </tr>
    );





  return (
    <div className="ml-3">
      <h5 className="subtitle text-left mb-2">{headerText}</h5>
      <form>
        <div className="form-group">
          <label htmlFor="userName">{t('common.username')}</label>
          <input
            type="text"
            className="form-control"
            value={userState.userName}
            onChange={(e) =>
              setUserState({ ...userState, userName: e.target.value })
            }
          />
          <input
            type="checkbox"
            className="form-check-input"
            checked={userState.isLocked}
            onChange={(e) =>
              setUserState({ ...userState, isLocked: e.target.checked })
            }
          />
          <label className="form-check-label" htmlFor="isLocked">
            {t('user.isLocked')}
          </label>
        </div>
        <div className="form-group">
          <label htmlFor="firstName">{t('user.firstName')}</label>
          <input
            type="text"
            className="form-control"
            value={userState.firstName}
            onChange={(e) =>
              setUserState({ ...userState, firstName: e.target.value })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="lastName">{t('user.lastName')}</label>
          <input
            type="text"
            className="form-control"
            value={userState.lastName}
            onChange={(e) =>
              setUserState({ ...userState, lastName: e.target.value })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="language">{t('user.language')}</label>
          <select
            className="form-control"
            value={userState.language}
            onChange={(e) =>
              setUserState({ ...userState, language: e.target.value })
            }
          >
            <option value="Finnish">{t('common.finnish')}</option>
            <option value="English">{t('common.english')}</option>
            <option value="Swedish">{t('common.swedish')}</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="roles">{t('user.roles')}</label>
          <table>
            <tbody>
              {hardCodedRoles.map((role) => (
                <tr key={role}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={role}
                      key={role}
                      value={role}
                      checked={userState.roles.some(c => c === role)}
                      onChange={handleRoleChange}
                    />
                    <label className="form-check-label" htmlFor={role}>{role}</label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
              </div>
              <div className="form-group">
                  <label htmlFor="userGroups">{t('user.userGroups')}</label>
                  <table>
                      <tbody>
                          {userGroups.map((userGroup) => (
                              <UserGroupRow key={userGroup.id} userGroup={userGroup} />
                          ))}
                      </tbody>
                  </table>
              </div>
        <div className="form-group">
          <label htmlFor="capabilities">{t('user.userCapabilities')}</label>
          <table>
            <tbody>
              {capabilities.map((capability) => (
                <CapabilityRow key={capability.id} capability={capability} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="form-group">
          <label htmlFor="stations">{t('user.stationsUserBelongs')}</label>
          <table>
            <tbody>
                {stations?.map((station) => (
                    <StationRow key={station.id} station={station} />              
              ))}
            </tbody>
          </table>
        </div>
        <div className="form-group">
          <label htmlFor="validFrom">{t('common.validFrom')}</label>
          <div>
            <DatePicker
              className="form-control"
              dateFormat={i18n.language === 'en' ? 'yyyy-MM-dd hh:mm' : 'dd.MM.yyyy HH:mm'}
              locale={i18n.language === 'fi' ? 'fi' : 'en'}
              selected={userState.validFrom}
              onChange={(date: Date | null) => date && setUserState({ ...userState, validFrom: date })}
              showTimeSelect
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="validTo">{t('common.validTo')}</label>
          <div>
            <DatePicker
              className="form-control"
              dateFormat={i18n.language === 'en' ? 'yyyy-MM-dd hh:mm' : 'dd.MM.yyyy HH:mm'}
              locale={i18n.language === 'fi' ? 'fi' : 'en'}
              selected={userState.validTo}
              onChange={(date: Date | null) => date && setUserState({ ...userState, validTo: date })}
              showTimeSelect
            />
          </div>
        </div>
        <button
          className="btn btn-primary mt-3 me-2 mb-2"
          disabled={!userState.userName || !userState.firstName || !userState.lastName || !userState.roles.length}
          onClick={onSubmit}>
          {t('buttons.save')}
        </button>
        {deleted && (
          <button
            className="btn btn-danger mt-3 me-2 mb-2"
            onClick={onDelete}>
            {t('buttons.delete')}
          </button>
        )}
        <button
          className="btn btn-danger mt-3 me-2 mb-2"
          onClick={onCancel}>
          {t('buttons.cancel')}
        </button>
        {userState.id !== '00000000-0000-0000-0000-000000000000' && (
          // Button to resend welcome message, only shown if user is not a new user
          <button
            className="btn btn-secondary mt-3 me-2 mb-2"
            onClick={onResendWelcomeMessage}
            disabled={resendWelcomeMessageMutation.isPending || isButtonDisabled}>
            {resendWelcomeMessageMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('buttons.sending')}
              </>
            ) : (
              t('buttons.resendWelcomeMessage')
            )}
          </button>
        )}
      </form>
    </div>
  );
};

export default UserForm;
